/**
 * loadGaussianScene: load a .sog / .lod-meta.json gaussian splat asset.
 *
 * The 180° Z rotation matches the splat coordinate system to PlayCanvas world
 * space (same convention as supersplat-viewer). `unified: true` selects the
 * unified gsplat renderer; the engine auto-detects streamed LOD when the URL
 * ends in lod-meta.json.
 *
 * NOTE: this resolves as soon as the asset is *parsed* (lod-meta.json + octree
 * ready) — NOT when the per-view chunks have streamed in. For a streamed scene
 * that means the visible splats keep refining after entry. Call
 * `preloadGaussianScene` (with the player spawn) to load the spawn-area fine
 * LOD + coarse coverage up front and drive a real load progress bar before
 * fading in.
 */

import { Asset, Entity, Vec3, WORKBUFFER_UPDATE_ALWAYS } from 'playcanvas';
import type { Application } from 'playcanvas';

export interface GaussianSceneHandle {
    entity: Entity;
    asset: Asset;
}

export interface GaussianLoadOptions {
    mobileLowQuality?: boolean;
    preloadStage?: 'coarse' | 'full' | 'near';
    /**
     * Preload a specific LOD index around the spawn instead of the
     * stage-dependent default. Used by the mobile background 'near' preload
     * to cap itself below the finest allowed LOD (bounded download/memory).
     */
    nearLod?: number;
    /** Meters around the spawn the 'near'/'full' preload covers. */
    nearRadius?: number;
}

// Finest LOD index mobile may select (0 = 100% density). 1 allows the 60%
// level; the 2 it replaced capped mobile at 40% density on top of the
// splatBudget balancer, which measurably pinned whole scenes at LOD4-6.
// 手机渲染上限（lodRangeMin）：不用 LOD0。真机 LOD 着色实测：1.2M 预算下
// 平衡器把额度全砸在脚下几个块（整片红=LOD0），LOD0 块体量又是 LOD1 的
// 数倍，近景清晰度并无感提升——用户拍板手机不要 LOD0。上限 LOD1：脚下
// 最细 LOD1，往远每级递增（LOD2/LOD3/…再往后由预算+最小像素筛压粗），
// 梯度台阶由预算平衡器「近端优先填充」天然形成。2026-09-22 当天四次：
// 1→2（LOD1 掉帧）→1（过糊）→0（近三级提档，脚下变红）→1（终值）。
const MOBILE_SHARED_HIGH_LOD_INDEX = 1;
const MOBILE_LEGACY_FINEST_LOD_COUNT = 4;

// The engine's SOG chunk streaming loader (GSplatAssetLoader) defaults to
// maxConcurrentLoads = 2 — one global FIFO pipe shared by the engine's own
// on-demand LOD requests and every ensureFileResource preload call. Measured
// on mainhall desktop: 43 near-field LOD0 preload chunks sat in that queue
// behind the engine's L2/L3 desires and ZERO LOD0 chunks had even started
// downloading 25s after entry. Widening the pipe is what lets the preload
// actually win the race.
const DESKTOP_CHUNK_LOAD_CONCURRENCY = 12;
const MOBILE_CHUNK_LOAD_CONCURRENCY = 6;
// 进场后后台阶段（full/near）的桌面并发：12 路并发下 LOD0 大块几乎同时到货，
// 各自的主线程整合（实例数据/纹理上传）会合并成一个 1.5s+ 的 Long Task
// （冷启动实测 1615ms）；降到 6 错峰到货，把单次冻结摊成多次小整合。队列
// FIFO 与 near-first 顺序不变，总下载时间不受影响（带宽瓶颈而非并发瓶颈）。
export const BACKGROUND_CHUNK_LOAD_CONCURRENCY = 6;

/**
 * 直接设置分块流送加载器的并发上限。引擎按需流送与各预载共用这条 FIFO
 * （见 DESKTOP_CHUNK_LOAD_CONCURRENCY 注释），进场优先级窗口用它把流送
 * 压到 0（流送队列暂停），给碰撞/形象这类单文件大下载让出全部带宽。
 */
export function setGaussianChunkConcurrency(
    handle: { entity: unknown; asset: Asset | null },
    value: number,
    reason: string
): void {
    const chunkLoader = (handle.asset?.resource as
        | { octree?: { assetLoader?: { maxConcurrentLoads?: number } } }
        | null)?.octree?.assetLoader;
    if (chunkLoader && typeof chunkLoader.maxConcurrentLoads === 'number' && chunkLoader.maxConcurrentLoads !== value) {
        chunkLoader.maxConcurrentLoads = value;
        console.info(`[gsplat] chunk loader maxConcurrentLoads=${value} (${reason})`);
    }
}

function mobilePreferredLod(asset: Asset): number {
    const resource = asset.resource as { octree?: { lodLevels?: number } } | null;
    const levels = resource?.octree?.lodLevels;
    if (typeof levels !== 'number' || levels <= 0) return 0;
    if (levels >= 7) {
        return Math.min(MOBILE_SHARED_HIGH_LOD_INDEX, levels - 1);
    }
    return Math.max(0, levels - MOBILE_LEGACY_FINEST_LOD_COUNT);
}

export function loadGaussianScene(
    app: Application,
    url: string,
    onProgress?: (fraction: number) => void,
    options: GaussianLoadOptions = {}
): Promise<GaussianSceneHandle> {
    return new Promise((resolve, reject) => {
        const filename = url.split('/').pop() || 'scene';
        const asset = new Asset(filename, 'gsplat', { url });
        console.info(`[gsplat-load] start url=${url} mobile=${Boolean(options.mobileLowQuality)}`);

        // Streamed SOG loads many chunk textures; forward per-asset download
        // progress (the lod-meta fetch). The bulk of "real" loading happens in
        // preloadGaussianScene after the spawn is known.
        asset.on('progress', (received: number, length: number) => {
            const fraction = length > 0 ? Math.min(0.2, 0.05 + 0.15 * received / length) : 0.05;
            console.info(`[gsplat-load] progress url=${url} received=${received} length=${length} fraction=${fraction.toFixed(3)}`);
            if (length > 0) onProgress?.(fraction);
        });

        asset.on('load', () => {
            try {
                const entity = new Entity('GaussianScene');
                // Flip splat space into PlayCanvas world space.
                entity.setLocalEulerAngles(0, 0, 180);
                entity.addComponent('gsplat', {
                    asset,
                    unified: true
                });
                // The gsplat stays on its default World layer (LAYERID_WORLD); the
                // camera includes that layer. Reassigning gsplat.layers here would
                // run before the entity is parented, and GSplatComponent.set layers
                // skips addToLayers() while the entity is not enabled in hierarchy —
                // leaving the splat in no layer (black screen).
                app.root.addChild(entity);

                // Mobile caps the finest allowed LOD, but keeps the far end adaptive.
                // NOTE: lodBaseDistance / lodMultiplier are deliberately not set —
                // PlayCanvas 2.20 deprecated them to no-op shims (their setters are
                // empty and the getters return 0). LOD selection is now driven by the
                // global `scene.gsplat.splatBudget` balancer (see createApp), so
                // setting them here would silently do nothing.
                const mobileLod = mobilePreferredLod(asset);
                if (entity.gsplat) {
                    entity.gsplat.lodRangeMin = options.mobileLowQuality ? mobileLod : 0;
                    entity.gsplat.lodRangeMax = Number.POSITIVE_INFINITY;
                }
                if (entity.gsplat && !options.mobileLowQuality) {
                    entity.gsplat.workBufferUpdate = WORKBUFFER_UPDATE_ALWAYS;
                }
                const resource = asset.resource as { octree?: { lodLevels?: number } } | null;
                console.info(`[gsplat-load] loaded url=${url} lodLevels=${String(resource?.octree?.lodLevels ?? 'unknown')} mobileLod=${mobileLod}`);

                // Widen the chunk-streaming pipe (see constant comment above).
                const chunkLoader = (asset.resource as { octree?: { assetLoader?: { maxConcurrentLoads?: number } } } | null)
                    ?.octree?.assetLoader;
                if (chunkLoader && typeof chunkLoader.maxConcurrentLoads === 'number') {
                    chunkLoader.maxConcurrentLoads = options.mobileLowQuality
                        ? MOBILE_CHUNK_LOAD_CONCURRENCY
                        : DESKTOP_CHUNK_LOAD_CONCURRENCY;
                    console.info(`[gsplat-load] chunk loader maxConcurrentLoads=${chunkLoader.maxConcurrentLoads}`);
                }

                resolve({ entity, asset });
            } catch (err) {
                console.error(`[gsplat-load] init failed url=${url}`, err);
                reject(err instanceof Error ? err : new Error(String(err)));
            }
        });

        asset.on('error', (err: unknown) => {
            console.warn(`[gsplat-load] error url=${url}`, err);
            reject(new Error(typeof err === 'string' ? err : `Failed to load ${url}`));
        });

        app.assets.add(asset);
        app.assets.load(asset);
    });
}

/** Scratch vector for the preload distance check. */
const _worldCenter = new Vec3();

/**
 * Preload streamed-gsplat chunks around the player spawn BEFORE entry so the
 * first view is covered (no black flash on the first turn). Drives `onProgress`
 * 0→1 with the blocking set's chunk-load ratio; the coarse entry preload must
 * complete; full/near background preloads may time out.
 *
 * Strategy — LOD3 entry, graded far (the chunk loader is a FIFO queue, so
 * ensure order is download priority order):
 *   - Entry blocking set (desktop 'coarse'): spawn area (≤30m) at LOD3 ONLY.
 *     2026-09-21 用户要求：出生点进场只要 LOD3、远处按距离 LOD4/5/6 —— 此前
 *     的「出生点核心圈 LOD0 阻塞」块体积是 LOD3 的数十倍，是进场景慢的主因。
 *     LOD0 精化不取消：桌面 'full' 后台预载与引擎 balancer（12M 预算）会在
 *     进场后继续把近场提到高清，画质不降，只是不再阻塞进入。
 *   - Far coverage (desktop 'coarse'/'full'): distance-graded LOD4 (≤60m) /
 *     LOD5 (≤120m) / LOD6 (beyond), enqueued non-blocking near→far. Mobile
 *     keeps the flat coarsest sweep (bounded download; the old mobile 'full'
 *     preload OOM-killed meihuashan, 781MB/125 chunks) and its separate
 *     bounded 'near' fine stage.
 */
export async function preloadGaussianScene(
    handle: GaussianSceneHandle,
    spawn: Vec3,
    onProgress?: (fraction: number) => void,
    options: GaussianLoadOptions = {}
): Promise<void> {
    const stage = options.preloadStage ?? 'full';
    const res = handle.asset.resource as {
        octree?: {
            lodLevels: number;
            nodes: { boundingSphere: Vec3; lods: { fileIndex: number }[] }[];
            ensureFileResource: (fileIndex: number) => void;
            getFileResource: (fileIndex: number) => unknown;
            assetLoader?: { maxConcurrentLoads?: number };
        };
    } | null;
    const octree = res?.octree;
    if (!octree || !octree.nodes?.length) {
        onProgress?.(1);
        return;
    }

    // 阶段化 chunk 并发（桌面）：coarse 是进场阻塞集，保持 12 路全力抢时间；
    // full/near 是后台精化，降到 BACKGROUND_CHUNK_LOAD_CONCURRENCY 错峰到货
    // （见常量注释）。手机保持 loadGaussianScene 设置的 6 路不动作。
    if (!options.mobileLowQuality) {
        const chunkLoader = octree.assetLoader;
        const target = stage === 'coarse' ? DESKTOP_CHUNK_LOAD_CONCURRENCY : BACKGROUND_CHUNK_LOAD_CONCURRENCY;
        if (chunkLoader && typeof chunkLoader.maxConcurrentLoads === 'number' && chunkLoader.maxConcurrentLoads !== target) {
            chunkLoader.maxConcurrentLoads = target;
            console.info(`[gsplat] chunk loader maxConcurrentLoads=${target} (stage=${stage})`);
        }
    }

    const maxLod = octree.lodLevels - 1;
    const fallbackLod = maxLod;
    const nearLod = options.nearLod ?? (options.mobileLowQuality ? mobilePreferredLod(handle.asset) : 0);
    // Desktop 30m matches the 12M splat budget's measured LOD0 reach
    // (~26.6m on laomendong2), so the background fine preload covers every
    // chunk the balancer will actually pin at LOD0 around the spawn.
    const nearRadius = options.nearRadius ?? (options.mobileLowQuality ? 34 : 30); // meters around spawn to preload visible detail
    // 出生点进场 LOD（2026-09-21 用户要求）：LOD3 即可看清脚下与近旁，块体积
    // 只有 LOD0 的几十分之一，进场等待从数十秒回到数秒；高清交给后台精化。
    const entryLod = Math.min(3, maxLod);
    const ensured = new Set<number>();

    // Distance-ordered passes (the chunk loader is a FIFO queue, so ensure
    // order is download priority order):
    //   Pass 1  spawn LOD3 (within radius, desktop) — the ONLY blocking set
    //   Pass 2  coarse: far coarsest only; full: far graded LOD4/5/6 queued AFTER
    //           the near LOD0 refine pass (FIFO ⇒ near refines before far upgrades)
    type NodeEntry = { node: { lods: { fileIndex: number }[] }; dist: number };
    const nodesByDistance: NodeEntry[] = octree.nodes
        .filter((node) => node.lods)
        .map((node) => ({ node, dist: nodeDistance(node, spawn) }))
        .sort((a, b) => a.dist - b.dist);
    const near = nodesByDistance.filter((e) => e.dist < nearRadius);
    const far = nodesByDistance.filter((e) => e.dist >= nearRadius);
    const ensurePass = (entries: NodeEntry[], lod: number, required?: Set<number>) => {
        for (const { node } of entries) {
            const fi = node.lods[Math.min(lod, maxLod)]?.fileIndex;
            if (fi == null || fi === -1) continue;
            if (!ensured.has(fi)) {
                octree.ensureFileResource(fi);
                ensured.add(fi);
            }
            required?.add(fi);
        }
    };

    // Pass 1 — 出生点 LOD3（桌面进场唯一阻塞集；mobile 保持近场最粗阻塞，
    // 精模延迟到独立 'near' 阶段，防 OOM）。
    const required = new Set<number>();
    if (options.mobileLowQuality) {
        ensurePass(near, fallbackLod, required);
        if (stage === 'near') ensurePass(near, nearLod, required);
    } else if (stage !== 'near') {
        // 桌面 coarse/full：出生点 LOD3 阻塞；full 阶段后台再把近场精化到
        // nearLod（LOD0），站定后画质提升但不拖慢进入。
        ensurePass(near, entryLod, required);
        if (stage === 'full') ensurePass(near, nearLod, required);
    }
    // Pass 2 — 远处覆盖。coarse 阶段只补最粗 LOD：文件极小，进场后远处立刻
    // 有画面、也几乎不占带宽；分级 LOD4/5/6 只在 full 阶段且排在近场 LOD0
    // 之后入队 —— chunk 队列是 FIFO，若进场时就把全场远端分级块排进队列，
    // 出生点的高清精化会被压到队尾，出现"远处先变清、脚下最后变清"的倒序
    // （2026-09-21 用户实测：出生蓝 LOD3 → 远处绿 LOD4/5 → 近处才红 LOD0）。
    if (stage === 'coarse') {
        ensurePass(far, fallbackLod);
    } else if (stage === 'full') {
        if (options.mobileLowQuality) {
            ensurePass(far, fallbackLod);
        } else {
            const grades: Array<[number, number]> = [
                [60, 4],
                [120, 5],
                [Number.POSITIVE_INFINITY, 6],
            ];
            for (const [radius, lod] of grades) {
                ensurePass(far.filter((e) => e.dist < radius), Math.min(lod, maxLod));
            }
        }
    }

    const fis = [...ensured];
    // Only the required set blocks entry; the rest keeps streaming in the
    // background after the scene fades in.
    const requiredFis = fis.filter((fi) => required.has(fi));
    const total = requiredFis.length;
    if (fis.length === 0 || total === 0) {
        onProgress?.(1);
        return;
    }
    const coverageLabel = stage === 'coarse'
        ? (options.mobileLowQuality
            ? `near coarse LOD ${fallbackLod} within ${nearRadius}m`
            : `spawn LOD ${entryLod} within ${nearRadius}m (blocking) + far coarsest LOD ${fallbackLod} streaming behind`)
        : stage === 'near'
            ? `near LOD ${nearLod} within ${nearRadius}m only`
            : `spawn LOD ${entryLod} + near LOD ${nearLod} refine + far graded, near-first`;
    console.log(`[gsplat] preloading ${fis.length} chunks (${stage}, ${coverageLabel}) around spawn ${spawn.x},${spawn.y},${spawn.z}; blocking on ${total}`);

    const POLL_MS = 50;
    const minRequiredLoaded = total;
    // The 'full' timeout was 15s; measured on mainhall that gave up at 2/45
    // chunks because the queue was still draining the engine's own mid-tier
    // requests. 60s lets a real network finish the near-field LOD0 set.
    // Desktop 'coarse' blocking set is spawn LOD3 only (small chunks); 20s is a
    // generous ceiling for slow networks — the wait ends on arrival anyway.
    const hardTimeoutMs = stage === 'coarse'
        ? (options.mobileLowQuality ? 30000 : 20000)
        : stage === 'near' ? 30000 : 60000;
    const now0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    let lastProgressLogMs = now0;
    let loaded = 0;
    let timedOut = false;
    while (true) {
        for (const fi of fis) if (!octree.getFileResource(fi)) octree.ensureFileResource(fi);
        loaded = requiredFis.filter((fi) => octree.getFileResource(fi)).length;
        onProgress?.(Math.max(0.2, Math.min(0.99, loaded / total)));
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        if (loaded >= minRequiredLoaded) break;
        if (now - lastProgressLogMs > 1000) {
            lastProgressLogMs = now;
            console.info(`[gsplat] preload progress (${stage}): ${loaded}/${total} chunks resident, need ${minRequiredLoaded}`);
        }
        if (now - now0 > hardTimeoutMs) {
            timedOut = true;
            console.warn(`[gsplat] preload timeout (${stage}): gave up with ${loaded}/${total} chunks resident, need ${minRequiredLoaded}`);
            if (stage === 'coarse') {
                throw new Error(`GSplat coarse preload timed out before first visible LOD coverage: ${loaded}/${total} chunks resident`);
            }
            break;
        }
        await new Promise((r) => setTimeout(r, POLL_MS));
    }
    onProgress?.(1);
    if (timedOut) {
        console.warn(`[gsplat] preload incomplete (${stage}): stopped at ${loaded}/${total} chunks resident; engine streaming continues`);
    } else {
        console.log(`[gsplat] preload done (${stage}): ${loaded}/${total} chunks resident`);
    }
}

/**
 * Distance from a splat-space octree node to a world-space point. The gsplat
 * entity only has the 180° Z rotation (no translation/scale), so
 * world = Rz(180) * splat = (-x, -y, z). (Done manually because the entity's
 * world transform may not be synced yet at preload time.)
 */
function nodeDistance(node: { boundingSphere?: Vec3 }, spawn: Vec3): number {
    const bs = node.boundingSphere;
    if (bs) {
        _worldCenter.set(-bs.x, -bs.y, bs.z);
    } else {
        _worldCenter.set(0, 0, 0);
    }
    return _worldCenter.distance(spawn);
}

export interface FollowPreloadHandle {
    stop(): void;
}

/**
 * 朝向跟随滚动预载（分环）：preloadGaussianScene 的所有 pass 都围绕出生点
 * 一次性排队，玩家走离出生点后，新区域的精档数据只剩引擎「看到才请求」的
 * 按需加载——转身朝向哪里，哪里先粗着、下载完才精化。本定时器跟随相机，
 * 同构——预载的档位就是引擎在该距离想渲染的档位，避免「预载了 LOD0 引擎
 * 同构——预载的档位就是引擎在该距离想渲染的档位，避免「预载了 LOD0 引擎
 * 却不选（预算不够）/ 引擎想要 LOD2 却没数据（只能显示粗档凑合）」的错配。
 * 镜头前方扇区（与 forward 夹角约 ±70°）优先 ensure，侧后方随后补齐，每
 * tick 限量入队不淹没 chunk 加载 FIFO；已在内存的块由 ensured 集合跳过，
 * 稳态常驻集 ≈ 引擎本来就要渲染的集合。渲染选档仍由引擎按投影尺寸+预算
 * 决定（同距离同档、视野外不渲染），本函数只让「朝向处、对应距离该有的
 * 档」的数据先到位。
 */
export function startFollowPreload(
    handle: GaussianSceneHandle,
    camera: { getPosition(): Vec3; forward: Vec3 },
    options: { mobileLowQuality?: boolean; radius?: number; lod?: number; rings?: Array<[number, number]>; intervalMs?: number; perTick?: number } = {}
): FollowPreloadHandle {
    const res = handle.asset.resource as {
        octree?: {
            lodLevels: number;
            nodes: { boundingSphere: Vec3; lods: { fileIndex: number }[] }[];
            ensureFileResource: (fileIndex: number) => void;
        };
    } | null;
    const octree = res?.octree;
    if (!octree?.nodes?.length) return { stop() { /* no octree */ } };
    const maxLod = octree.lodLevels - 1;
    // 分环表 [半径m, LOD] 按序匹配第一个命中的环（=前方扇区的档位）；侧后
    // 方（含视线外的脚下）在环档上自动粗一档。不传 rings 退化为「半径内单一
    // lod、无方向差」（桌面 30m/LOD0 即此路径）。手机传
    // [[6,1],[12,2],[20,3],[32,4],[48,5]]：注视方向近环 LOD1（=渲染上限，
    // 不用 LOD0）逐级外推，脚下/周边 +1 档起步。
    const rings: Array<[number, number]> = options.rings?.length
        ? options.rings
        : [[options.radius ?? (options.mobileLowQuality ? 12 : 30), Math.min(options.lod ?? (options.mobileLowQuality ? mobilePreferredLod(handle.asset) : 0), maxLod)]];
    const maxRadius = rings.reduce((m, [r]) => Math.max(m, r), 0);
    const ringLodOf = (dist: number): number => {
        for (const [r, lod] of rings) if (dist <= r) return Math.min(lod, maxLod);
        return -1; // 超出所有环：交给引擎按需加载（预算+最小像素筛管远处）
    };
    const perTick = options.perTick ?? (options.mobileLowQuality ? 8 : 20);
    const center = new Vec3();
    const ensured = new Set<number>();
    let active = true;
    let timer = 0;
    const stop = (): void => { active = false; if (timer) window.clearInterval(timer); timer = 0; };
    const tick = (): void => {
        if (!active) return;
        try {
            const camPos = camera.getPosition();
            const fwd = camera.forward;
            const front: { node: { lods: { fileIndex: number }[] }; dist: number; lod: number }[] = [];
            const back: typeof front = [];
            for (const node of octree.nodes) {
                if (!node.lods) continue;
                const bs = node.boundingSphere;
                center.set(bs ? -bs.x : 0, bs ? -bs.y : 0, bs ? bs.z : 0);
                const dist = center.distance(camPos);
                const base = ringLodOf(dist);
                if (base < 0) continue;
                // 朝向权重：节点方向与相机前向夹角约 ±70° 内算前方扇区；侧后
                // 方（含视线之外的脚下）粗一档——最高精度只留给注视方向，脚下
                // 省下的额度让给视线前方，与 foveation 中心加权、
                // lodBehindPenalty（>60° 离轴加粗）三者同向。
                const dot = dist > 1e-4 ? center.sub(camPos).dot(fwd) / dist : 1;
                const inFront = dot > 0.35;
                const lod = Math.min(base + (inFront ? 0 : 1), maxLod);
                (inFront ? front : back).push({ node, dist, lod });
            }
            front.sort((a, b) => a.dist - b.dist);
            back.sort((a, b) => a.dist - b.dist);
            let queued = 0;
            for (const { node, lod } of [...front, ...back]) {
                if (queued >= perTick) break;
                const fi = node.lods[lod]?.fileIndex;
                if (fi == null || fi === -1 || ensured.has(fi)) continue;
                octree.ensureFileResource(fi);
                ensured.add(fi);
                queued++;
            }
        } catch (err) {
            // 资产已卸载等异常：停掉自身，避免对旧 octree 持续 ensure。
            console.warn('[gsplat-follow] preload tick failed, stopping:', err);
            stop();
        }
    };
    tick();
    timer = window.setInterval(tick, options.intervalMs ?? 1200);
    return { stop };
}